import { InputNumber, Form } from "@mimber/ui-web"
import {
  useController,
  useFormContext,
  FieldValues,
  FieldPath,
} from "react-hook-form"
import type { CurrencyInputFieldProps } from "./CurrencyInputField.types"
import {
  centsToDollars,
  dollarsToCents,
  centsToMinMax,
} from "./CurrencyInputField.utils"

/**
 * CurrencyInputField — stores value in cents, displays in dollars.
 *
 * The RHF form value is always an integer number of cents (e.g. 2500).
 * The input displays and accepts dollar amounts (e.g. 25.00) and converts
 * transparently on change.
 */
export function CurrencyInputField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  required,
  help,
  placeholder,
  disabled,
  minCents,
  maxCents,
  currencySymbol = "$",
}: CurrencyInputFieldProps<TFieldValues, TName>) {
  const { control: contextControl } = useFormContext<TFieldValues>()
  const formControl = control ?? contextControl

  const { field, fieldState } = useController({ control: formControl, name })

  const displayValue = centsToDollars(field.value as number | null | undefined)

  const handleChange = (dollars: number | string | null) => {
    field.onChange(dollarsToCents(dollars) ?? undefined)
  }

  return (
    <Form.Item
      label={label}
      required={required}
      help={fieldState.error?.message ?? help}
      validateStatus={fieldState.error ? "error" : undefined}
    >
      <InputNumber
        value={displayValue}
        onChange={handleChange}
        onBlur={field.onBlur}
        ref={field.ref}
        placeholder={placeholder ?? "0.00"}
        disabled={disabled}
        prefix={currencySymbol}
        precision={2}
        step={0.01}
        min={centsToMinMax(minCents)}
        max={centsToMinMax(maxCents)}
        style={{ width: "100%" }}
      />
    </Form.Item>
  )
}
